import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone, OnInit} from '@angular/core';
import {SemRestRequestService} from '../../../../../../projects/api/src/sem-rest-request/sem-rest-request.service';
import {ApiList} from '../../../../API/api-list.enum';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {IsAuthorizedService} from '../../../services/is-authorized.service';
import {FavoritesApiService, IFavoriteObject} from '../../../../../../projects/api/src/favorite/favorites-api.service';
import {FavoriteModel, FavoriteType} from '../../../../../../projects/api/src/favorite/favorite.model';

/**
 * Компонент служит для отображения иконки "звезда"
 * Важно: при переходе в дашбордах не отрабатывают события роутера, поэтому
 * используется кэширование ссылок и принудительная проверка на изменение урла каждые 1000мс
 */
@Component({
    selector: 'app-star-icon',
    templateUrl: './star-icon.component.html',
    styleUrls: ['./star-icon.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class StarIconComponent implements OnInit {
  public displayModalDialog = false;
  public favLinkForm: FormGroup;
  public selected = false;
  public linkId = -1;
  private favLinksCache: FavoriteModel[] = [];
  private intervalId;

  favorite: IFavoriteObject;

  FavoriteType = FavoriteType;

  constructor(
    private request: SemRestRequestService,
    private fb: FormBuilder,
    private ref: ChangeDetectorRef,
    private router: Router,
    private zone: NgZone,
    private isAuthorizedService: IsAuthorizedService,
    private favoritesApiService: FavoritesApiService,
    private route: ActivatedRoute,
  ) {
    this.route.url.subscribe((val) => {
      this.favoritesApiService.setFavoriteObject();
    });
  }

  ngOnInit() {
    this.createForm();
    this.isFavLinkInLocation();
    this.router.events.subscribe((r) => {
      if (r instanceof NavigationEnd) {
        this.isFavLinkInLocation(true);
      }
    });
    // Нужно так как роутер в AngularJS не всегда отдает обновление URL, чтобы не нагружать CD выносим за зону
    this.zone.runOutsideAngular(() => this.intervalId = setInterval(() => this.isFavLinkInLocation(true), 2000));

    this.favoritesApiService.favorite.subscribe((val) => {
      this.favorite = val;
      this.favLinkForm.reset({url: document.location.href, checked: true, description: this.favorite.title});
    });
  }

  /**
   * Инициализирует первоначальное состояние формы
   */
  createForm() {
    this.favLinkForm = this.fb.group({
      url: document.location.href,
      description: '',
      checked: true,
    });
  }

  /**
   * Проверяет является ли текущая страница избранной ссылкой
   * @param {boolean} useCache - использовать кэш
   */
  isFavLinkInLocation(useCache: boolean = false) {
    if (!this.isAuthorizedService.isAuthorized.getValue()) {
      clearInterval(this.intervalId);
      return;
    }
    if (useCache) {
      const fl = this.favLinksCache.find((favLink) => favLink.url.toString() === document.location.href.toString());
      if (fl) {
        this.selected = true;
        this.linkId = fl.id;
      } else {
        this.selected = false;
        this.linkId = -1;
      }
      this.ref.markForCheck();
      return;
    }

    this.favoritesApiService.getAll().subscribe((favLinks) => {
      this.favLinksCache = favLinks;
      const fl = favLinks.find((favLink) => favLink.url.toString() === document.location.href.toString() ||
        (favLink.linktype === this.favorite.type &&
          `${favLink.linkobjid}` === this.favorite.id));
      if (fl) {
        this.selected = true;
        this.linkId = fl.id;
      } else {
        this.selected = false;
        this.linkId = -1;
      }
      this.ref.markForCheck();
    });
  }

  /**
   * Удаляет текущую страницу из списка избранного
   */
  deleteFavLink() {
    const fav = (this.linkId > 0 && this.favLinksCache.length > 0) ? this.favLinksCache.filter(f => f.id === this.linkId) : [];
    if (this.linkId > 0 && confirm(`Delete selected items: ${fav.length === 1 ? fav[0].description : this.linkId}`)) {
      this.favLinksCache = [];
      this.request
        .baseUrlAppend(ApiList.Db, ApiList.User, ApiList.Profile, ApiList.Favorites)
        .deleteById(this.linkId)
        .subscribe(() => {
          this.isFavLinkInLocation();
        });
    }
  }

  /**
   * Показывает диалог для добавления текущей страницы в избранное
   */
  showEditDialog() {
    this.displayModalDialog = true;
    this.ref.markForCheck();
  }

  /**
   * Скрывает диалог по добавлению страницы
   */
  hideEditDialog() {
    this.displayModalDialog = false;
    this.isFavLinkInLocation();
  }

  onChangeChecked($event, value) {
    console.log('onChangeChecked', $event.target.value, value);
  }

  /**
   * Подготавливает и направляет запрос на сервер для добавления ссылки в избранное
   */
  addFavoriteLink() {

    const object = this.favLinkForm.value;
    const path = object.url.split('/');
    const id = path[path.length - 1];
    if (object.url.indexOf('main/dashboard') !== -1 && parseInt(id, 10) + '' === id) {
      object.linktype = 'EXDASHBOARD';
      object.linkobjid = parseInt(id, 10);
    }
    const params: Omit<FavoriteModel, 'id'> = {url: object.url, description: object.description, linkobjid: undefined, linktype: FavoriteType.URL};

    if (this.favorite.id && this.favorite.type && object.checked) {
      params.linktype = this.favorite.type;
      params.linkobjid = parseInt(this.favorite.id, 10);
    }
    this.favoritesApiService.create(params).subscribe(() => {
      this.hideEditDialog();
    });
  }

}
